import React, { useState, useEffect } from 'react'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  MagnifyingGlassIcon,
  // FunnelIcon,
  TagIcon,
  // PhotoIcon,
  Bars3Icon,
  ChevronUpIcon,
  ChevronDownIcon,
  FolderIcon,
  FolderOpenIcon,
  // AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline'
import { useCategories } from '../contexts/CategoryContext'

interface Category {
  id: string
  name: string
  nameAr?: string
  description?: string
  descriptionAr?: string
  parentId?: string
  image?: string
  icon?: string
  color?: string
  isActive: boolean
  sortOrder: number
  itemCount: number
  defaultPriority?: number
  createdAt: Date
  updatedAt: Date
  children?: Category[]
}

interface MenuItem {
  id: string
  name: string
  categoryId: string
  subcategoryId?: string
  isActive: boolean
}

const CategoryManagement: React.FC = () => {
  const { categories, addCategory, updateCategory, deleteCategory, refreshCategories } = useCategories()
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [newCategory, setNewCategory] = useState<Partial<Category>>({})
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [sortBy, setSortBy] = useState<'name' | 'order' | 'items'>('order')
  const [viewMode, setViewMode] = useState<'tree' | 'grid' | 'list'>('tree')

  useEffect(() => {
    loadMenuItems()
  }, [])


  const loadMenuItems = () => {
    // Mock menu items for counting
    const mockItems: MenuItem[] = [
      { id: '1', name: 'Chicken Shawarma', categoryId: '1-1', isActive: true },
      { id: '2', name: 'Beef Burger', categoryId: '1-2', isActive: true },
      { id: '3', name: 'Hummus Plate', categoryId: '2-1', isActive: true },
      { id: '4', name: 'Fresh Juice', categoryId: '3-2', isActive: true },
      { id: '5', name: 'Baklava', categoryId: '4', isActive: true },
      { id: '6', name: 'French Fries', categoryId: '5', isActive: true }
    ]
    setMenuItems(mockItems)
  }

  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.description?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  const sortedCategories = [...filteredCategories].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name)
      case 'items':
        return b.itemCount - a.itemCount
      case 'order':
      default:
        return a.sortOrder - b.sortOrder
    }
  })

  const toggleCategoryExpansion = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId)
    } else {
      newExpanded.add(categoryId)
    }
    setExpandedCategories(newExpanded)
  }

  const handleAddCategory = async () => {
    if (newCategory.name) {
      try {
        await addCategory({
          name: newCategory.name!,
          nameAr: newCategory.nameAr,
          description: newCategory.description,
          descriptionAr: newCategory.descriptionAr,
          parentId: newCategory.parentId,
          image: newCategory.image,
          icon: newCategory.icon,
          color: newCategory.color || '#6B7280',
          isActive: newCategory.isActive ?? true,
          sortOrder: newCategory.sortOrder || categories.length + 1,
          defaultPriority: newCategory.defaultPriority || 1,
          children: []
        })

        setNewCategory({})
        setShowAddModal(false)
      } catch (error) {
        console.error('Error adding category:', error)
        alert('Failed to add category. Please try again.')
      }
    }
  }

  const handleEditCategory = async () => {
    if (selectedCategory && newCategory.name) {
      try {
        await updateCategory(selectedCategory.id, {
          ...newCategory,
          name: newCategory.name!
        })
        setSelectedCategory(null)
        setNewCategory({})
        setShowEditModal(false)
      } catch (error) {
        console.error('Error updating category:', error)
        alert('Failed to update category. Please try again.')
      }
    }
  }

  const handleDeleteCategory = async () => {
    if (selectedCategory) {
      // Check if category has items
      const hasItems = menuItems.some(item => 
        item.categoryId === selectedCategory.id || 
        selectedCategory.children?.some(child => child.id === item.categoryId)
      )

      if (hasItems) {
        alert('Cannot delete category with menu items. Please move items to another category first.')
        return
      }

      try {
        await deleteCategory(selectedCategory.id)
        setSelectedCategory(null)
        setShowDeleteModal(false)
      } catch (error) {
        console.error('Error deleting category:', error)
        const message = error instanceof Error ? error.message : 'Failed to delete category. Please try again.'
        alert(message)
      }
    }
  }

  const moveCategory = (categoryId: string, direction: 'up' | 'down') => {
    const categoryIndex = categories.findIndex(cat => cat.id === categoryId)
    if (categoryIndex === -1) return

    const targetIndex = direction === 'up' ? categoryIndex - 1 : categoryIndex + 1

    if (targetIndex >= 0 && targetIndex < categories.length) {
      const category = categories[categoryIndex]
      const targetCategory = categories[targetIndex]
      
      // Swap sort orders
      const tempSortOrder = category.sortOrder
      updateCategory(category.id, { sortOrder: targetCategory.sortOrder })
      updateCategory(targetCategory.id, { sortOrder: tempSortOrder })
      
      // Refresh to see the changes
      refreshCategories()
    }
  }

  const toggleCategoryStatus = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId)
    if (category) {
      updateCategory(categoryId, { isActive: !category.isActive })
    }
  }

  const CategoryCard = ({ category, level = 0 }: { category: Category, level?: number }) => (
    <div 
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-lg transition-shadow ${
        level > 0 ? 'ml-6 border-l-4 border-l-blue-200 dark:border-l-blue-800' : ''
      }`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg mr-3"
              style={{ backgroundColor: category.color }}
            >
              {category.icon || '📁'}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center">
                {category.name}
                {level > 0 && (
                  <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                    Subcategory
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {category.description || 'No description'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              category.isActive 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}>
              {category.isActive ? 'Active' : 'Inactive'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {category.itemCount} items
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Order: {category.sortOrder} • Created: {category.createdAt.toLocaleDateString()}
          </div>
          <div className="flex items-center space-x-2">
            {level === 0 && (
              <>
                <button
                  onClick={() => moveCategory(category.id, 'up')}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Move up"
                >
                  <ChevronUpIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => moveCategory(category.id, 'down')}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Move down"
                >
                  <ChevronDownIcon className="h-4 w-4" />
                </button>
              </>
            )}
            <button
              onClick={() => toggleCategoryStatus(category.id)}
              className={`p-1 rounded ${
                category.isActive 
                  ? 'text-green-500 hover:text-green-600' 
                  : 'text-gray-400 hover:text-green-500'
              }`}
              title={category.isActive ? 'Deactivate' : 'Activate'}
            >
              <EyeIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                setSelectedCategory(category)
                setNewCategory(category)
                setShowEditModal(true)
              }}
              className="p-1 rounded text-blue-500 hover:text-blue-600"
              title="Edit"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                setSelectedCategory(category)
                setShowDeleteModal(true)
              }}
              className="p-1 rounded text-red-500 hover:text-red-600"
              title="Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  const CategoryTreeItem = ({ category, level = 0 }: { category: Category, level?: number }) => {
    const hasChildren = category.children && category.children.length > 0
    const isExpanded = expandedCategories.has(category.id)

    return (
      <div className="mb-2">
        <div className="flex items-center p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow">
          <div className="flex items-center flex-1">
            {hasChildren && (
              <button
                onClick={() => toggleCategoryExpansion(category.id)}
                className="mr-3 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                )}
              </button>
            )}
            {!hasChildren && <div className="w-6 mr-3" />}
            
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm mr-3"
              style={{ backgroundColor: category.color }}
            >
              {category.icon || '📁'}
            </div>
            
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 dark:text-white">
                {category.name}
                {level > 0 && (
                  <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                    Subcategory
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {category.description || 'No description'} • {category.itemCount} items
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              category.isActive 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}>
              {category.isActive ? 'Active' : 'Inactive'}
            </span>
            
            <div className="flex items-center space-x-1">
              <button
                onClick={() => toggleCategoryStatus(category.id)}
                className={`p-1 rounded ${
                  category.isActive 
                    ? 'text-green-500 hover:text-green-600' 
                    : 'text-gray-400 hover:text-green-500'
                }`}
                title={category.isActive ? 'Deactivate' : 'Activate'}
              >
                <EyeIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  setSelectedCategory(category)
                  setNewCategory(category)
                  setShowEditModal(true)
                }}
                className="p-1 rounded text-blue-500 hover:text-blue-600"
                title="Edit"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  setSelectedCategory(category)
                  setShowDeleteModal(true)
                }}
                className="p-1 rounded text-red-500 hover:text-red-600"
                title="Delete"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="ml-6 mt-2 space-y-2">
            {category.children?.map(child => (
              <CategoryTreeItem key={child.id} category={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate">
            Category Management
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Organize your menu with categories and subcategories
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={() => {
              setSelectedCategory(null)
              setNewCategory({})
              setShowAddModal(true)
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Category
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FolderIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Total Categories
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {categories.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FolderOpenIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Active Categories
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {categories.filter(c => c.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TagIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Subcategories
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {categories.reduce((sum, cat) => sum + (cat.children?.length || 0), 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Bars3Icon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Total Items
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {categories.reduce((sum, cat) => sum + cat.itemCount, 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="order">Sort by Order</option>
            <option value="name">Sort by Name</option>
            <option value="items">Sort by Items</option>
          </select>

          <div className="flex space-x-1 bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('tree')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'tree'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Tree
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {/* Categories Display */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        {viewMode === 'tree' ? (
          <div className="space-y-2">
            {sortedCategories.map(category => (
              <CategoryTreeItem key={category.id} category={category} />
            ))}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedCategories.map(category => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {sortedCategories.map(category => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        )}

        {sortedCategories.length === 0 && (
          <div className="text-center py-12">
            <FolderIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No categories found
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm ? 'Try adjusting your search terms' : 'Create your first category to get started'}
            </p>
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add New Category
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newCategory.name || ''}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter category name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category Name (Arabic) <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={newCategory.nameAr || ''}
                  onChange={(e) => setNewCategory({ ...newCategory, nameAr: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="اسم الفئة (اختياري)"
                  dir="rtl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newCategory.description || ''}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Describe the category..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (Arabic) <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <textarea
                  value={newCategory.descriptionAr || ''}
                  onChange={(e) => setNewCategory({ ...newCategory, descriptionAr: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="وصف الفئة (اختياري)"
                  dir="rtl"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Parent Category
                  </label>
                  <select
                    value={newCategory.parentId || ''}
                    onChange={(e) => setNewCategory({ ...newCategory, parentId: e.target.value || undefined })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Main Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Icon
                  </label>
                  <input
                    type="text"
                    value={newCategory.icon || ''}
                    onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="🍽️"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Color
                  </label>
                  <input
                    type="color"
                    value={newCategory.color || '#6B7280'}
                    onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={newCategory.sortOrder || categories.length + 1}
                    onChange={(e) => setNewCategory({ ...newCategory, sortOrder: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default Priority (for waiter orders)
                </label>
                <input
                  type="number"
                  min="1"
                  value={newCategory.defaultPriority || 1}
                  onChange={(e) => setNewCategory({ ...newCategory, defaultPriority: Number(e.target.value) })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="1"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Lower numbers = higher priority (1 is served first)
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newCategory.isActive ?? true}
                  onChange={(e) => setNewCategory({ ...newCategory, isActive: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCategory}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Add Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Category
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newCategory.name || ''}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter category name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category Name (Arabic) <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={newCategory.nameAr || ''}
                  onChange={(e) => setNewCategory({ ...newCategory, nameAr: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="اسم الفئة (اختياري)"
                  dir="rtl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newCategory.description || ''}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Describe the category..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (Arabic) <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <textarea
                  value={newCategory.descriptionAr || ''}
                  onChange={(e) => setNewCategory({ ...newCategory, descriptionAr: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="وصف الفئة (اختياري)"
                  dir="rtl"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Icon
                  </label>
                  <input
                    type="text"
                    value={newCategory.icon || ''}
                    onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="🍽️"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Color
                  </label>
                  <input
                    type="color"
                    value={newCategory.color || '#6B7280'}
                    onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={newCategory.sortOrder || 0}
                  onChange={(e) => setNewCategory({ ...newCategory, sortOrder: Number(e.target.value) })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default Priority (for waiter orders)
                </label>
                <input
                  type="number"
                  min="1"
                  value={newCategory.defaultPriority || 1}
                  onChange={(e) => setNewCategory({ ...newCategory, defaultPriority: Number(e.target.value) })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="1"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Lower numbers = higher priority (1 is served first)
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newCategory.isActive ?? true}
                  onChange={(e) => setNewCategory({ ...newCategory, isActive: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditCategory}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Category
            </h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to delete "{selectedCategory.name}"?
              </p>
              
              {selectedCategory.itemCount > 0 && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-3">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    ⚠️ This category contains {selectedCategory.itemCount} menu items. 
                    You must move these items to another category before deleting.
                  </p>
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCategory}
                disabled={selectedCategory.itemCount > 0}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CategoryManagement
